import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaPlus, FaTrophy, FaMinus } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import classNames from 'classnames';
import { format } from 'date-fns';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import {
  getAllScoreCards,
  updateScoreCardScores,
  ScoreCard,
  ScoreCardScoreModel,
  ScoreCardType,
} from '../../../../services/scoreCardService';
import ToggleSwitch from '../../../../components/misc/ToggleSwitch';
import {
  clampScore,
  DEFAULT_SCORE_CARD_TYPE,
  formatScore,
  formatScoreForInput,
  getScoreCardTypeLabelKey,
  getScoreInputProps,
  getTimeInputPlaceholder,
  isCompleteTimeInput,
  isTimeScoreCardType,
  normalizeTimeInput,
  parseScore,
  validateScore,
} from '../../../../utils/scoreCardUtil';

import Avatar from '../../../../components/misc/Avatar';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import ScoreCardModal from '../../../../components/modals/CreateScoreCardModal';
import EditScoreCardModal from '../../../../components/modals/EditScoreCardModal';
import Button from '../../../../components/misc/Button';
import HorizontalSponsorsList from '../../../../components/misc/HorizontalSponsorsList';

export default function CommunityScoresPage() {
  const { t } = useTranslation();
  const community = useSelectedCommunity();

  const [scoreCards, setScoreCards] = useState<ScoreCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<ScoreCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editScoreCardOpen, setEditScoreCardOpen] = useState(false);
  const [editingCardForModal, setEditingCardForModal] = useState<ScoreCard | null>(null);

  const canCreateScores = React.useMemo(() => {
    if (!community) return false;
    return community.role === 'ADMIN' || !!community.memberPermissions?.STATS_AND_SCORES?.CREATE;
  }, [community]);

  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (!community?.id) return;

    setLoading(true);

    getAllScoreCards(community.id)
      .then((data) => {
        setScoreCards(data || []);
        // Select the first card by default
        if (data && data.length > 0) {
          setSelectedCard(data[0]);
        }
      })
      .catch((error) => {
        console.error('Failed to load score cards:', error);
        toast.error(t('page.community.scores.loadError'));
      })
      .finally(() => setLoading(false));
  }, [community?.id, t]);

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.scores.loading')}</p>
      </div>
    );
  }

  const isEmpty = scoreCards.length === 0 && !loading;

  // Create modal state helper implemented above
  // function setShowCreateModal is replaced by state setter `setShowCreateModal`

  return (
    <div className="flex flex-col gap-4">
      <CommunityHeader
        title={t('page.community.tabs.scores')}
        action={
          !isEmpty ? (
            <div className="flex items-center gap-2">
              {canCreateScores && (
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90 md:w-auto">
                  <FaPlus />
                  {t('page.community.scores.create')}
                </button>
              )}
            </div>
          ) : undefined
        }
      />

      <HorizontalSponsorsList placement="SCORECARDS" />

      <ScoreCardModal
        open={showCreateModal}
        setOpen={setShowCreateModal}
        communityId={community?.id}
        onCreated={(newCard) => {
          setScoreCards((prev) => [...prev, newCard]);
          setSelectedCard(newCard);
          setShowCreateModal(false);
        }}
      />

      <EditScoreCardModal
        open={editScoreCardOpen}
        setOpen={(open) => {
          if (!open) setEditingCardForModal(null);
          setEditScoreCardOpen(open);
        }}
        card={editingCardForModal}
        onSave={(updated) => {
          setScoreCards((prev) => prev.map((c) => (c.scoreCardId === updated.scoreCardId ? updated : c)));
          if (selectedCard?.scoreCardId === updated.scoreCardId) {
            setSelectedCard(updated);
          }
        }}
      />

      {scoreCards.length === 0 ? (
        <div className="flex-1">
          <div className="flex flex-col items-center justify-center gap-4 py-12">
            <FaTrophy className="mb-4 h-16 w-16 text-primary" />
            <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.scores')}</h3>
            {canCreateScores && (
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2 text-white transition-colors hover:bg-opacity-90 md:w-auto">
                <FaPlus />
                {t('page.community.scores.create')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-6 md:flex-row">
          {/* Sidebar - Score Card Selection */}
          <div className="w-full flex-shrink-0 md:w-96">
            <div className="overflow-hidden rounded-lg border border-secondary-50 bg-secondary">
              <div className="border-b border-secondary-50 bg-secondary-200 px-4 py-3">
                <h3 className="font-semibold text-primary">{t('page.community.scores.scoreCards')}</h3>
              </div>
              <div className="flex flex-col">
                {scoreCards.map((card) => {
                  const canEditCard = Boolean(
                    community?.role === 'ADMIN' ||
                      community?.memberPermissions?.STATS_AND_SCORES?.UPDATE ||
                      card.memberEditAccess,
                  );

                  return (
                    <div
                      key={card.scoreCardId}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedCard(card)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') setSelectedCard(card);
                      }}
                      className={`border-secondary-100 flex items-center gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-secondary-50 ${
                        selectedCard?.scoreCardId === card.scoreCardId
                          ? 'border-l-4 border-l-primary bg-primary bg-opacity-10'
                          : ''
                      }`}>
                      <FaTrophy className="flex-shrink-0 text-primary" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-primary">{card.name}</p>
                        <p className="text-secondary-500 text-xs">
                          {t(`page.community.scores.forms.type.${getScoreCardTypeLabelKey(card.type)}`)}
                        </p>
                        <p className="text-secondary-500 text-xs">
                          {card.scores.length} {t('page.community.scores.members')}
                        </p>
                      </div>

                      {canEditCard && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCardForModal(card);
                            setEditScoreCardOpen(true);
                          }}
                          aria-label={t('general.edit')}
                          className="ml-2 rounded bg-secondary-50 p-2 text-primary">
                          <FiEdit2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content - Podium and List */}
          <div className="flex flex-1 flex-col gap-4">
            {selectedCard && <HorizontalSponsorsList scoreCardId={selectedCard.scoreCardId} />}
            {selectedCard ? (
              <ScoreCardSection
                card={selectedCard}
                editMode={editMode}
                onSetEditMode={setEditMode}
                onUpdateCard={(newCard) => {
                  setScoreCards((prev) => prev.map((c) => (c.scoreCardId === newCard.scoreCardId ? newCard : c)));
                  setSelectedCard(newCard);
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <FaTrophy className="text-secondary-300 mb-4 h-12 w-12" />
                <p className="text-secondary-600">{t('page.community.scores.selectCard')}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ScoreCardSectionProps {
  card: ScoreCard;
  onUpdateCard?: (newCard: ScoreCard) => void;
  editMode?: boolean;
  onSetEditMode?: (value: boolean) => void;
}

function ScoreCardSection({ card, onUpdateCard, editMode, onSetEditMode }: ScoreCardSectionProps) {
  const { t } = useTranslation();

  const sortedScores = [...card.scores].sort((a, b) => {
    return card.ascending ? a.score - b.score : b.score - a.score;
  });

  const topThree = sortedScores.slice(0, 3);
  const first = topThree[0];
  const second = topThree[1];
  const third = topThree[2];

  const community = useSelectedCommunity();
  const [pendingScores, setPendingScores] = useState<Record<number, number>>({});
  const [saving, setSaving] = useState(false);
  const [cancelVersion, setCancelVersion] = useState(0);

  const canEditCard = Boolean(
    community?.role === 'ADMIN' || community?.memberPermissions?.STATS_AND_SCORES?.UPDATE || card.memberEditAccess,
  );

  const handleScoreChange = (memberId: number, newScore: number) => {
    setPendingScores((prev) => ({ ...prev, [memberId]: newScore }));
  };

  useEffect(() => {
    const saveAllScores = async () => {
      if (!community?.id || Object.keys(pendingScores).length === 0) return;

      setSaving(true);
      try {
        const scoresPayload = Object.entries(pendingScores).map(([memberIdStr, newScore]) => ({
          memberId: Number(memberIdStr),
          score: Number(newScore),
        }));
        await updateScoreCardScores(community.id!, card.scoreCardId, scoresPayload);

        const refreshed = await getAllScoreCards(community.id!);
        const updatedCard = refreshed.find((c) => c.scoreCardId === card.scoreCardId);
        if (updatedCard) {
          onUpdateCard?.(updatedCard);
          setPendingScores({});
        } else {
          toast.error(t('page.community.scores.loadError'));
        }
      } catch (err: any) {
        toast.error(err.message === 'UNAUTHORIZED' ? t('errors.unauthorized') : t('page.community.scores.loadError'));
      } finally {
        setSaving(false);
      }
    };

    if (editMode === false && Object.keys(pendingScores).length > 0) {
      saveAllScores();
    }
  }, [editMode, pendingScores, community?.id, card.scoreCardId, onUpdateCard, t]);

  return (
    <div className="flex flex-col gap-6">
      {/* Podium Section */}
      {topThree.length >= 3 && (
        <div className="flex flex-row items-end justify-center gap-2">
          <PodiumPlace score={second} place={2} size="medium" scoreType={card.type} />
          <PodiumPlace score={first} place={1} size="large" isFirst scoreType={card.type} />
          <PodiumPlace score={third} place={3} size="small" scoreType={card.type} />
        </div>
      )}

      {/* Score Table */}
      <div className="overflow-hidden rounded-xl">
        <div className="flex items-center justify-between rounded-t-xl border border-secondary-50 bg-secondary-200 px-4 py-4 md:px-6">
          <div>
            <h2 className="text-xl font-semibold text-primary">{card.name}</h2>
            {!!card.description && <p className="text-sm text-details/70">{card.description}</p>}
            <p className="text-sm text-details/70">
              {t(`page.community.scores.forms.type.${getScoreCardTypeLabelKey(card.type)}`)}
            </p>
          </div>

          {canEditCard && (
            <div className="flex items-center gap-2">
              {!editMode ? (
                <Button variant="primary" onClick={() => onSetEditMode?.(true)}>
                  {t('page.community.scores.editScores')}
                  <FiEdit2 className="h-4 w-4" />
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setPendingScores({});
                      setCancelVersion((v) => v + 1);
                      onSetEditMode?.(false);
                    }}>
                    {t('general.cancel')}
                  </Button>
                  <Button type="button" variant="primary" onClick={() => onSetEditMode?.(false)} disabled={saving}>
                    {t('general.save')}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full table-auto rounded-b-xl border border-secondary-50">
            <thead>
              <tr className="text-left text-sm text-details/60">
                <th className="w-full min-w-0 overflow-hidden px-2 py-3 font-medium md:px-6">
                  {t('page.community.scores.name')}
                </th>
                <th className="w-20 min-w-0 px-2 py-3 text-center font-medium md:px-6">
                  {t('page.community.scores.scoreNumber')}
                </th>
                <th className="hidden w-28 px-2 py-3 text-right font-medium md:table-cell md:px-6">
                  {t('page.community.scores.lastUpdated')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedScores.map((s, index) => (
                <ScoreRow
                  key={`${card.scoreCardId}_${s.memberId}`}
                  card={card}
                  scoreData={s}
                  index={index}
                  editMode={editMode}
                  cancelVersion={cancelVersion}
                  onScoreChange={(memberId, newScore) => handleScoreChange(memberId, newScore)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ScoreRow({
  card,
  scoreData,
  index,
  editMode,
  cancelVersion,
  onScoreChange,
}: {
  card: ScoreCard;
  scoreData: ScoreCardScoreModel;
  index: number;
  editMode?: boolean;
  cancelVersion?: number;
  onScoreChange?: (memberId: number, newScore: number) => void;
}) {
  const { t } = useTranslation();
  const community = useSelectedCommunity();
  const scoreType = card.type ?? DEFAULT_SCORE_CARD_TYPE;
  const [score, setScore] = useState<string>(formatScoreForInput(scoreData.score, scoreType));

  useEffect(() => {
    if (cancelVersion !== undefined) {
      setScore(formatScoreForInput(scoreData.score, scoreType));
    }
  }, [cancelVersion, scoreData.score, scoreType]);

  const canEditThis = Boolean(
    community?.role === 'ADMIN' || community?.memberPermissions?.STATS_AND_SCORES?.UPDATE || card.memberEditAccess,
  );

  useEffect(() => {
    setScore(formatScoreForInput(scoreData.score, scoreType));
  }, [scoreData.score, scoreType]);

  const submitScoreValue = (nextScore: number) => {
    const clampedScore = clampScore(nextScore, scoreType);
    if (!validateScore(clampedScore, scoreType)) {
      toast.error(t('page.community.scores.forms.errors.invalidScore'));
      return;
    }

    setScore(formatScoreForInput(clampedScore, scoreType));
    onScoreChange?.(scoreData.memberId, clampedScore);
  };

  const commitScoreInput = () => {
    if (scoreType === ScoreCardType.BOOLEAN) {
      return;
    }

    if (isTimeScoreCardType(scoreType) && !isCompleteTimeInput(score, scoreType)) {
      toast.error(
        t('page.community.scores.forms.errors.invalidTimeFormat', { format: getTimeInputPlaceholder(scoreType) }),
      );
      setScore(formatScoreForInput(scoreData.score, scoreType));
      return;
    }

    const parsedScore = parseScore(score, scoreType);
    const clampedScore = clampScore(parsedScore, scoreType);

    if (!validateScore(clampedScore, scoreType)) {
      toast.error(t('page.community.scores.forms.errors.invalidScore'));
      setScore(formatScoreForInput(scoreData.score, scoreType));
      return;
    }

    setScore(formatScoreForInput(clampedScore, scoreType));
    onScoreChange?.(scoreData.memberId, clampedScore);
  };

  const numericInputProps = getScoreInputProps(scoreType);

  return (
    <tr className="border-secondary-100 border-t">
      <td className="min-w-0 overflow-hidden px-2 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-secondary-500 hidden w-6 flex-shrink-0 text-sm md:inline-flex">{index + 1}</span>
          <Avatar src={scoreData.picture} alias={`${scoreData.firstName || ''} ${scoreData.lastName || ''}`} />
          <div className="min-w-0 flex-1 overflow-hidden">
            <span className="block truncate text-xs font-medium text-primary md:text-sm">
              {scoreData.firstName} {scoreData.lastName}
            </span>
          </div>
        </div>
      </td>
      <td className="min-w-0 px-4 py-4 md:px-6">
        {canEditThis && editMode ? (
          <div className="flex items-center justify-center gap-2">
            {scoreType === ScoreCardType.BOOLEAN ? (
              <div className="w-28">
                <ToggleSwitch
                  leftLabel={t('page.community.scores.forms.no')}
                  rightLabel={t('page.community.scores.forms.yes')}
                  value={Number(score) > 0}
                  onChange={(value) => submitScoreValue(value ? 1 : 0)}
                />
              </div>
            ) : (
              <>
                {!isTimeScoreCardType(scoreType) && (
                  <button
                    type="button"
                    onClick={() => submitScoreValue((parseScore(score, scoreType) || 0) - 1)}
                    className="flex-shrink-0 rounded-full bg-primary-300 p-1 text-lg text-secondary-50">
                    <FaMinus />
                  </button>
                )}

                <div className="relative inline-block">
                  <input
                    id={`score-input-${card.scoreCardId}-${scoreData.memberId}`}
                    type={isTimeScoreCardType(scoreType) ? 'text' : 'number'}
                    className="no-spinners h-8 w-24 max-w-full !rounded-xl !bg-secondary-50 !bg-opacity-100 text-center md:w-28"
                    value={score}
                    placeholder={isTimeScoreCardType(scoreType) ? getTimeInputPlaceholder(scoreType) : ''}
                    step={numericInputProps.step}
                    min={numericInputProps.min}
                    max={numericInputProps.max}
                    onChange={(ev) => {
                      if (isTimeScoreCardType(scoreType)) {
                        setScore(normalizeTimeInput(ev.target.value, scoreType));
                        return;
                      }
                      setScore(ev.target.value);
                    }}
                    onBlur={commitScoreInput}
                  />
                </div>

                {!isTimeScoreCardType(scoreType) && (
                  <button
                    type="button"
                    onClick={() => submitScoreValue((parseScore(score, scoreType) || 0) + 1)}
                    className="flex-shrink-0 rounded-full bg-primary-300 p-1 text-lg text-secondary-50">
                    <FaPlus />
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <span className="block w-28 truncate text-center font-medium text-primary">
            {formatScore(scoreData.score, scoreType, t)}
          </span>
        )}
      </td>

      <td className="text-secondary-600 hidden px-4 py-4 text-right md:table-cell md:px-6">
        {format(new Date(scoreData.lastUpdated), 'd MMM')}
      </td>
    </tr>
  );
}

interface PodiumPlaceProps {
  score: ScoreCardScoreModel | undefined;
  place: number;
  size: 'small' | 'medium' | 'large';
  isFirst?: boolean;
  scoreType?: ScoreCardType;
}

function PodiumPlace({ score, place, size, isFirst = false, scoreType }: PodiumPlaceProps) {
  if (!score) return null;

  const { t } = useTranslation();
  const effectiveScoreType = scoreType ?? score.type ?? DEFAULT_SCORE_CARD_TYPE;

  const sizeConfig = {
    small: {
      container: 'w-12 h-12 md:w-16 md:h-16',
      avatar: 'w-8 h-8 md:w-12 md:h-12',
      marginTop: 'mt-2',
    },
    medium: {
      container: 'w-16 h-16 md:w-20 md:h-20',
      avatar: 'w-12 h-12 md:w-14 md:h-14',
      marginTop: 'mt-1',
    },
    large: {
      container: 'w-20 h-20 md:w-24 md:h-24',
      avatar: 'w-14 h-14 md:w-18 md:h-18',
      marginTop: 'mt-0',
    },
  };

  const config = sizeConfig[size];

  return (
    <div className={`flex flex-col items-center ${config.marginTop}`}>
      <div className="flex flex-col items-center">
        <div className={`${config.container} z-10 -mb-3 flex items-center justify-center rounded-full`}>
          <Avatar
            src={score.picture}
            alias={`${score.firstName || ''} ${score.lastName || ''}`}
            className={classNames(config.avatar, 'h-full w-full text-2xl')}
          />
        </div>

        {/* Podium step below avatar */}
        <div
          className={classNames(
            size === 'large' ? 'w-20 md:w-24' : size === 'medium' ? 'w-16 md:w-20' : 'w-12 md:w-16',
            place === 1 ? 'h-6 md:h-8' : place === 2 ? 'h-5 md:h-6' : 'h-4 md:h-5',
            'bg-primary',
            'flex items-end justify-center rounded-t-md',
          )}>
          <span className={classNames('text-white', 'mb-1 text-xs font-semibold')}>{place}</span>
        </div>
      </div>

      {/* Name */}
      <p
        className={`mt-1 text-center font-semibold text-primary ${isFirst ? 'text-sm md:text-base' : 'text-xs md:text-sm'}`}>
        {score.firstName} {score.lastName}
      </p>

      {/* Score */}
      <p className="text-secondary-500 text-sm">{formatScore(score.score, effectiveScoreType, t)}</p>
    </div>
  );
}
